/**
 * React hook for responsive layout system
 */

import { useState, useEffect } from 'react';
import {
  LayoutConstants,
  LayoutMode,
  UseLayoutReturn,
  getCurrentLayout,
  getLayoutMode,
  setLayoutMode,
  initializeLayoutSystem,
} from '@restaurant/core';

/**
 * Hook to access and respond to layout changes
 */
// Initialize layout system eagerly so the first render uses the correct
// viewport-based mode instead of the static default ('mobile').
if (typeof window !== 'undefined') {
  initializeLayoutSystem();
}

export function useLayout(): UseLayoutReturn {
  const [layout, setLayout] = useState<LayoutConstants>(getCurrentLayout());
  const [mode, setMode] = useState<LayoutMode>(getLayoutMode());

  useEffect(() => {
    // Re-initialize in case it wasn't done at module load (SSR)
    initializeLayoutSystem();

    // Update state with current values
    setLayout(getCurrentLayout());
    setMode(getLayoutMode());

    // Listen for layout mode changes
    const handleLayoutChange = (event: CustomEvent) => {
      const { mode: newMode, layout: newLayout } = event.detail;
      setMode(newMode);
      setLayout(newLayout);
    };

    window.addEventListener('layoutModeChange', handleLayoutChange as EventListener);

    // Also listen for resize events as backup
    const handleResize = () => {
      const newMode = getLayoutMode();
      if (newMode !== mode) {
        setLayoutMode(newMode);
        setLayout(getCurrentLayout());
        setMode(newMode);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('layoutModeChange', handleLayoutChange as EventListener);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    layout,
    mode,
    isMobile: mode === 'mobile',
    isDesktop: mode === 'desktop',
  };
}